import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AppService,
  CanvasService,
  DetectionService,
  SimulationService,
  TaskService,
  CameraService,
} from '@services';
import { SceneStore } from '@stores';
import '@tensorflow/tfjs-backend-webgl';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { debounceTime } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

const SECONDS_TO_SKIP = 5;

@Component({
  selector: 'bo-simulation-view',
  templateUrl: './simulation-view.component.html',
  standalone: true,
  imports: [AngularSvgIconModule, CommonModule, FormsModule],
})
export class SimulationViewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  video = viewChild<ElementRef>('video');
  recording = viewChild<ElementRef>('recording');
  screen = viewChild<ElementRef>('screen');
  overlayCanvas = viewChild<ElementRef>('overlayCanvas');
  dynamicCanvas = viewChild<ElementRef>('dynamicCanvas');
  debugCanvas = viewChild<ElementRef>('debugCanvas');
  canvasElement = viewChild<ElementRef>('canvasElement');

  protected appService = inject(AppService);
  protected cameraService = inject(CameraService);
  protected canvasService = inject(CanvasService);
  private detectionService = inject(DetectionService);
  private destroyRef = inject(DestroyRef);
  protected sceneStore = inject(SceneStore);
  protected taskService = inject(TaskService);
  public simulationService = inject(SimulationService);

  videoStream: MediaStream;
  screenshotTimeOut: NodeJS.Timeout;
  isStarted = false;
  isRunning?: boolean;
  randomNum?: string;
  intervalId: number;

  constructor() {
    this.killStreams();
    toObservable(this.appService.demoMode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.swapVideoElement();
      });

    this.cameraService.getCameras();

    effect(() => {
      if (this.appService.cameraId()) {
        this.swapVideoElement();
      }
    });

    effect(
      () => {
        if (this.appService.demoMode()) {
          this.simulationService.state.set('simulation');
        }
      },
      { allowSignalWrites: true },
    );

    toObservable(this.canvasService.screen)
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.updateOverlays();
      });
  }

  swapVideoElement() {
    const interval = setInterval(() => {
      if (this.overlayCanvas()) {
        if (!this.appService.demoMode()) {
          navigator.mediaDevices
            .getUserMedia({
              audio: false,
              video: this.appService.cameraConfig(),
            })
            .then((stream) => {
              this.videoStream = stream;
              this.recording().nativeElement.srcObject = stream;
              this.recording().nativeElement.play();

              const camRatio = [
                stream.getVideoTracks()[0].getSettings().width,
                stream.getVideoTracks()[0].getSettings().height,
              ];

              const width = Math.max(camRatio[0], camRatio[1]);
              const height = Math.min(camRatio[0], camRatio[1]);

              this.canvasService.camera.set({ width, height });

              this.updateOverlays();
            })
            .catch();
          this.detectionService.detectionSources.set(
            'insideView',
            this.recording(),
          );
          clearInterval(interval);
        } else {
          this.killStreams();
          this.detectionService.detectionSources.set(
            'insideView',
            this.video(),
          );
          this.canvasService.video.set({
            width: this.video().nativeElement.videoWidth,
            height: this.video().nativeElement.videoHeight,
          });
          if (this.canvasService.video()) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = this.canvasService.video().width;
            canvas.height = this.canvasService.video().height;

            const drawVideoFrame = () => {
              ctx.drawImage(
                this.video().nativeElement,
                0,
                0,
                canvas.width,
                canvas.height,
              );
              requestAnimationFrame(drawVideoFrame);
            };

            this.video().nativeElement.addEventListener('play', () => {
              drawVideoFrame();
            });

            this.videoStream = canvas.captureStream();
          }

          this.updateOverlays();
          clearInterval(interval);
        }
      }
    }, 200);
  }

  updateOverlays() {
    const shift = this.canvasService.shift();
    this.overlayCanvas()!.nativeElement.width =
      this.canvasService.overlay().width;
    this.overlayCanvas()!.nativeElement.height =
      this.canvasService.overlay().height;

    this.overlayCanvas()!.nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';

    this.dynamicCanvas().nativeElement.width =
      this.canvasService.overlay().width;
    this.dynamicCanvas().nativeElement.height =
      this.canvasService.overlay().height;
    this.dynamicCanvas().nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';

    this.debugCanvas().nativeElement.width = this.canvasService.overlay().width;
    this.debugCanvas().nativeElement.height =
      this.canvasService.overlay().height;
    this.debugCanvas().nativeElement.style.transform =
      'translate(' + shift.x + 'px, ' + shift.y + 'px)';
  }

  fastForward() {
    this.video().nativeElement.currentTime =
      this.video().nativeElement.currentTime + SECONDS_TO_SKIP;
  }

  fastBackward() {
    this.video().nativeElement.currentTime =
      this.video().nativeElement.currentTime - SECONDS_TO_SKIP;
  }

  pauseVideo() {
    if (this.video().nativeElement.paused) {
      this.video().nativeElement.play();
    } else {
      this.video().nativeElement.pause();
    }
  }

  replayVideo() {
    if (this.video().nativeElement.paused) this.video().nativeElement.play();
    else this.video().nativeElement.load();
  }

  killStreams() {
    this.videoStream?.getTracks().forEach((track) => {
      if (track) {
        track.stop();
        this.videoStream.removeTrack(track);
      }
    });
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.pauseCamera();
    } else {
      this.resumeCamera();
    }
  }

  pauseCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((track) => (track.enabled = false));
    }
  }

  resumeCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((track) => (track.enabled = true));
    }
  }

  onFinish(): void {
    this.appService.fullScreen.set(false);
    this.isRunning = false;
  }

  ngOnInit(): void {
    this.randomNum = uuidv4();

    document.onkeydown = (event) => {
      switch (event.key) {
        case 'ArrowLeft': {
          this.fastBackward();
          break;
        }
        case 'ArrowRight': {
          this.fastForward();
          break;
        }
        case 'Space': {
          this.pauseVideo();
          break;
        }
      }
    };

    this.appService.fastBackward
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fastBackward();
      });

    this.appService.fastForward
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fastForward();
      });

    this.appService.pauseVideo
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pauseVideo();
      });

    this.appService.replayVideo
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.replayVideo();
      });

    this.simulationService.isSimulationRunning.set(true);
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this),
    );
    window.addEventListener('beforeunload', (event) => {
      event.returnValue = true;
    });
  }

  ngAfterViewInit() {
    this.simulationService.startDetection();
  }

  ngOnDestroy(): void {
    clearTimeout(this.intervalId);
    this.simulationService.isSimulationRunning.set(false);
    this.killStreams();
  }
}
